import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategoryScroll() {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const remaining = categories.slice(3);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  const images = [
    'https://readdy.ai/api/search-image?query=A%20stylish%20denim%20jacket%20hanging%20on%20an%20exposed%20brick%20wall%20with%20industrial%20loft%20vibe%2C%20warm%20vintage%20lighting%2C%20urban%20fashion%20editorial%20style%2C%20textured%20surfaces%2C%20moody%20atmosphere%20with%20soft%20contrast&width=600&height=600&seq=scroll-v2-jackets&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Linen%20trousers%20casually%20laid%20on%20a%20rattan%20mat%20with%20tropical%20palm%20leaf%20shadows%2C%20bright%20sunny%20day%2C%20resort%20wear%20aesthetic%2C%20vacation%20vibes%2C%20relaxed%20lifestyle%20product%20photography&width=600&height=600&seq=scroll-v2-pants&orientation=squarish',
    'https://readdy.ai/api/search-image?query=A%20retro%20baseball%20cap%20placed%20on%20a%20vintage%20wooden%20crate%20in%20a%20sunlit%20garden%20setting%2C%20wildflowers%20in%20soft%20focus%20background%2C%20outdoor%20lifestyle%20photography%2C%20warm%20summer%20evening%20glow&width=600&height=600&seq=scroll-v2-headwear&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Coordinated%20outfit%20set%20displayed%20on%20a%20minimalist%20clothing%20rack%20in%20a%20bright%20white%20studio%2C%20professional%20fashion%20presentation%2C%20clean%20lines%2C%20soft%20diffused%20lighting%2C%20modern%20boutique%20aesthetic&width=600&height=600&seq=scroll-v2-sets&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Soft%20organic%20cotton%20underwear%20set%20folded%20on%20a%20marble%20surface%20with%20eucalyptus%20branches%2C%20spa-like%20luxury%20aesthetic%2C%20soft%20morning%20light%2C%20serene%20atmosphere%2C%20premium%20product%20photography&width=600&height=600&seq=scroll-v2-underwear&orientation=squarish',
    'https://readdy.ai/api/search-image?query=A%20vibrant%20sports%20jersey%20draped%20over%20a%20locker%20room%20bench%20with%20dynamic%20stadium%20lighting%2C%20action%20sports%20aesthetic%2C%20bold%20colors%2C%20athletic%20energy%2C%20professional%20sports%20photography%20style&width=600&height=600&seq=scroll-v2-jersey&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Elegant%20golden%20jewelry%20and%20watch%20pieces%20scattered%20artistically%20on%20dark%20slate%20stone%20surface%2C%20dramatic%20spotlight%20creating%20luxurious%20reflections%2C%20high-end%20jewelry%20photography%2C%20dark%20moody%20aesthetic&width=600&height=600&seq=scroll-v2-accessories&orientation=squarish',
    'https://readdy.ai/api/search-image?query=A%20structured%20leather%20tote%20bag%20standing%20on%20a%20minimalist%20concrete%20surface%20with%20architectural%20shadows%2C%20modern%20urban%20aesthetic%2C%20harsh%20daylight%20creating%20geometric%20patterns%2C%20contemporary%20fashion%20photography&width=600&height=600&seq=scroll-v2-bags&orientation=squarish',
    'https://readdy.ai/api/search-image?query=A%20handcrafted%20leather%20belt%20coiled%20on%20a%20weathered%20wooden%20workbench%20with%20artisan%20tools%20in%20soft%20focus%2C%20workshop%20ambiance%2C%20warm%20tungsten%20light%2C%20handmade%20craftsmanship%20photography&width=600&height=600&seq=scroll-v2-belts&orientation=squarish',
    'https://readdy.ai/api/search-image?query=A%20luxury%20fragrance%20bottle%20on%20a%20mirror%20surface%20reflecting%20soft%20candlelight%2C%20rose%20petals%20scattered%20around%2C%20intimate%20sensual%20atmosphere%2C%20premium%20beauty%20product%20photography%2C%20warm%20amber%20tones&width=600&height=600&seq=scroll-v2-perfumes&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Eclectic%20collection%20of%20unique%20lifestyle%20products%20on%20a%20colorful%20Moroccan%20tile%20table%2C%20bohemian%20style%2C%20warm%20afternoon%20sunlight%2C%20artistic%20composition%2C%20travel%20and%20culture%20inspired%20photography&width=600&height=600&seq=scroll-v2-other&orientation=squarish',
  ];

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              {t('categories.title')}{' '}
              <span className="text-accent-500">—</span>
            </h2>
            <p className="mt-2 text-sm text-foreground-600">{t('categories.subtitle')}</p>
          </div>
          <span className="hidden md:inline-block px-4 py-1.5 bg-foreground-950 text-white text-xs font-label rounded-full uppercase tracking-wider">
            {t('categories.all')}
          </span>
        </div>

        <div className="relative">
          <button
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200/70 text-foreground-700 hover:text-foreground-950 hover:border-background-300/60 transition-colors cursor-pointer shadow-sm"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {remaining.map((cat, idx) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex-shrink-0 w-[260px] bg-white rounded-lg border border-background-200/70 overflow-hidden hover:border-background-300/60 transition-colors cursor-pointer group"
              >
                <div className="aspect-square overflow-hidden bg-background-100">
                  <img
                    src={images[idx] || images[0]}
                    alt={cat.label}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{cat.label}</h3>
                  <p className="mt-1 text-xs text-foreground-500">Коллекция</p>
                </div>
              </a>
            ))}
          </div>

          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200/70 text-foreground-700 hover:text-foreground-950 hover:border-background-300/60 transition-colors cursor-pointer shadow-sm"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}